import { ReadonlyNonEmptyArray } from 'fp-ts/ReadonlyNonEmptyArray';
import { ReactNode, useMemo, useState } from 'react';
import { ProgressIndicator, ProgressIndicatorProps, Stack, StackProps } from '@optimus-web/core';

export interface StepProps<S extends string> {
    currentStep: S;
    setCurrentStep: (step: S) => void;
}

export interface StepProgressIndicatorProps<S extends string>
    extends Omit<ProgressIndicatorProps<S>, 'activeStep' | 'children' | 'steps'> {
    steps: ReadonlyNonEmptyArray<S>;
    stepLabels: Record<S, ReactNode>;
    optionalSteps?: Partial<Record<S, boolean>>;
}

export interface SteppedContentProps<S extends string> extends StepProgressIndicatorProps<S> {
    children: ({
        matchSteps,
        currentStep,
        setCurrentStep,
    }: { matchSteps: (matchingMap: Record<S, () => ReactNode>) => ReactNode } & StepProps<S>) => ReactNode;
    spacing?: StackProps['spacing'];
}

export const StepProgressIndicator = <S extends string>({
    steps,
    stepLabels,
    optionalSteps,
    currentStep,
    setCurrentStep,
    onStepChange,
    ...other
}: StepProgressIndicatorProps<S> & StepProps<S>) => {
    const currentStepIndex = steps.indexOf(currentStep);
    return (
        <ProgressIndicator
            steps={steps.map((step, index) => ({
                value: step,
                label: stepLabels[step],
                optional: optionalSteps ? optionalSteps[step] : false,
                completed: index < currentStepIndex,
                editable: index < currentStepIndex,
            }))}
            activeStep={currentStep}
            onStepChange={(step: S) => {
                onStepChange?.(step);
                setCurrentStep(step);
            }}
            {...other}
        />
    );
};

export const SteppedContent = <S extends string>({
    children,
    spacing = '400',
    steps,
    ...other
}: SteppedContentProps<S>) => {
    const [currentStep, setCurrentStep] = useState<S>(steps[0]);
    return (
        <Stack spacing={spacing} vertical fill>
            <StepProgressIndicator steps={steps} currentStep={currentStep} setCurrentStep={setCurrentStep} {...other} />
            {children({ matchSteps: (matchingMap) => matchingMap[currentStep](), currentStep, setCurrentStep })}
        </Stack>
    );
};

export const useSteppedContent = <S extends string>({ steps }: Pick<StepProgressIndicatorProps<S>, 'steps'>) => {
    const [currentStep, setCurrentStep] = useState<S>(steps[0]);
    return {
        StepProgressIndicator: useMemo(
            () => (props: Omit<StepProgressIndicatorProps<S>, 'steps'>) => (
                <StepProgressIndicator
                    steps={steps}
                    currentStep={currentStep}
                    setCurrentStep={setCurrentStep}
                    {...props}
                />
            ),
            [currentStep, steps]
        ),
        matchSteps: (matchingMap: Record<S, () => ReactNode>) => matchingMap[currentStep](),
        state: {
            currentStep,
            setCurrentStep,
        },
    };
};
